import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 60vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-bottom: 6px;
  align-items: center;
`;

const Select = styled.select`
  padding: 6px 10px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Title = styled.h3`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  text-align: center;
  color: #666;
  margin-bottom: 12px;
  position: relative;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover { color: #000; }

  svg { width: 18px; height: 18px; }
`;

// --- helpers ---
const dayOfYear = (date) => {
  const y = date.getFullYear();
  const start = new Date(y, 0, 0);
  return Math.floor((date - start) / (1000 * 60 * 60 * 24)); // 1..365/366
};

const daysInYear = (y) => (new Date(y, 1, 29).getMonth() === 1 ? 366 : 365);

const DailyUniqueClientsChart = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    const years = Array.from(
      new Set(
        data
          .map((item) => {
            const d = new Date(item.DataWystawienia);
            return isNaN(d) ? null : d.getFullYear();
          })
          .filter(Boolean)
      )
    ).sort((a, b) => b - a);

    setAvailableYears(years);
    if (!selectedYear && years.length > 0) setSelectedYear(years[0]);
  }, [data, selectedYear]);

  const processedData = useMemo(() => {
    if (!selectedYear) return [];

    // Build sets per day for selected and previous year
    const currSets = Array.from({ length: daysInYear(selectedYear) + 1 }, () => new Set());
    const prevYear = selectedYear - 1;
    const prevSets = Array.from({ length: daysInYear(prevYear) + 1 }, () => new Set());

    data.forEach((item) => {
      const d = new Date(item.DataWystawienia);
      if (isNaN(d)) return;

      const key = item.NrKontrahenta || item.NazwaKontrahenta || String(item.NIP) || String(item.IdKontrahenta) || "unknown";

      const y = d.getFullYear();
      const doy = dayOfYear(d); // 1-based

      if (y === selectedYear && currSets[doy]) {
        currSets[doy].add(key);
      } else if (y === prevYear && prevSets[doy]) {
        prevSets[doy].add(key);
      }
    });

    // Use max of both year lengths so X axis aligns
    const maxDays = Math.max(daysInYear(selectedYear), daysInYear(prevYear));

    const out = [];
    for (let i = 1; i <= maxDays; i++) {
      out.push({
        day: i,
        curr: currSets[i] ? currSets[i].size : 0,
        prev: prevSets[i] ? prevSets[i].size : 0,
      });
    }
    return out;
  }, [data, selectedYear]);

  if (!availableYears.length) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="daily-unique-tooltip">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="daily-unique-tooltip"
        place="left"
        content={
          <>
            <strong>Unikalni klienci dziennie</strong>
            <br />
            Porównanie {selectedYear} vs {selectedYear - 1}.<br />
            Każdy punkt to liczba unikalnych klientów danego dnia.
          </>
        }
      />

      <FiltersWrapper>
        <label>
          Rok:{" "}
          <Select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </label>
      </FiltersWrapper>

      <Title>Unikalni klienci dziennie – {selectedYear} vs {selectedYear - 1}</Title>

      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={processedData}>
          <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
          <XAxis dataKey="day" tick={{ fontSize: 10 }} />
          <YAxis allowDecimals={false} />
          <Tooltip
            formatter={(value, name) => [
              value,
              name === "curr" ? `Klienci (${selectedYear})` : `Klienci (${selectedYear - 1})`,
            ]}
            labelFormatter={(label) => `Dzień roku: ${label}`}
          />
          <Legend
            payload={[
              { id: "curr", type: "line", value: `Klienci (${selectedYear})`, color: "#2e7d32" },
              { id: "prev", type: "line", value: `Klienci (${selectedYear - 1})`, color: "#fb8c00" },
            ]}
          />
          {/* current year → GREEN */}
          <Line
            type="monotone"
            dataKey="curr"
            stroke="#2e7d32"
            name={`Klienci (${selectedYear})`}
            dot={false}
            strokeWidth={2}
          />
          {/* previous year → ORANGE */}
          <Line
            type="monotone"
            dataKey="prev"
            stroke="#fb8c00"
            name={`Klienci (${selectedYear - 1})`}
            dot={false}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
};

export default DailyUniqueClientsChart;
